/**
 * 统一的多语言映射管理
 * 
 * 此文件是 mentorai_home 项目中所有语言相关配置的唯一来源。
 * 所有需要使用语言映射的地方都应该从这里导入。
 * 
 * 注意：此文件的语言定义必须与后端 app/core/lang.py 保持一致。
 */

/**
 * 语言配置接口
 */
export interface LanguageOption {
  /** 语言代码，如 "zh-CN", "en" */
  value: string;
  /** 语言的本地化名称，如 "简体中文", "English" */
  text: string;
  /** 用于某些组件的额外 key 字段 */
  key?: string;
}

/**
 * 语言映射对象接口
 */
export type LanguageMap = Record<string, string>;

/**
 * 所有支持的语言代码枚举
 */
export enum LanguageCode {
  /** 简体中文 */
  ZH_CN = 'zh',
  /** 英语 */
  EN = 'en',
  /** 繁体中文 */
  ZH_TW = 'tw',
  /** 日语 */
  JA = 'ja',
  /** 韩语 */
  KO = 'ko',
  /** 泰语 */
  TH = 'th',
  /** 越南语 */
  VI = 'vi',
  /** 印尼语 */
  ID = 'id',
  /** 印地语 */
  HI = 'hi',
  /** 西班牙语 */
  ES = 'es',
  /** 法语 */
  FR = 'fr',
  /** 德语 */
  DE = 'de',
  /** 葡萄牙语 */
  PT = 'pt',
  /** 俄语 */
  RU = 'ru',
  /** 阿拉伯语 */
  AR = 'ar',
}

/**
 * 完整的语言映射表（所有支持的语言）
 * 语言代码 -> 本地化名称
 */
export const LANGUAGE_MAP: LanguageMap = {
  [LanguageCode.ZH_CN]: '简体中文',
  [LanguageCode.EN]: 'English',
  [LanguageCode.ZH_TW]: '繁体中文',
  [LanguageCode.JA]: '日本語',
  [LanguageCode.KO]: '한국어',
  [LanguageCode.TH]: 'ไทย',
  [LanguageCode.VI]: 'Tiếng Việt',
  [LanguageCode.ID]: 'Bahasa Indonesia',
  [LanguageCode.HI]: 'हिन्दी',
  [LanguageCode.ES]: 'Español',
  [LanguageCode.FR]: 'Français',
  [LanguageCode.DE]: 'Deutsch',
  [LanguageCode.PT]: 'Português',
  [LanguageCode.RU]: 'Русский',
  [LanguageCode.AR]: 'العربية',
};

/**
 * 带表情符号的语言映射（用于更友好的显示）
 */
export const LANGUAGE_MAP_WITH_EMOJI: LanguageMap = {
  [LanguageCode.ZH_CN]: '🇨🇳 简体中文',
  [LanguageCode.EN]: '🇺🇸 English',
  [LanguageCode.ZH_TW]: '🇹🇼 繁体中文',
  [LanguageCode.JA]: '🇯🇵 日本語',
  [LanguageCode.KO]: '🇰🇷 한국어',
  [LanguageCode.ES]: '🇪🇸 Español',
  [LanguageCode.FR]: '🇫🇷 Français',
  [LanguageCode.DE]: '🇩🇪 Deutsch',
  [LanguageCode.PT]: '🇵🇹 Português',
  [LanguageCode.RU]: '🇷🇺 Русский',
  [LanguageCode.TH]: '🇹🇭 ไทย',
  [LanguageCode.VI]: '🇻🇳 Tiếng Việt',
  [LanguageCode.ID]: '🇮🇩 Bahasa Indonesia',
  [LanguageCode.HI]: '🇮🇳 हिन्दी',
  [LanguageCode.AR]: '🇸🇦 العربية',
};

/**
 * 所有支持的语言选项数组
 */
export const ALL_LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: LanguageCode.ZH_CN, text: LANGUAGE_MAP[LanguageCode.ZH_CN] },
  { value: LanguageCode.EN, text: LANGUAGE_MAP[LanguageCode.EN] },
  { value: LanguageCode.ZH_TW, text: LANGUAGE_MAP[LanguageCode.ZH_TW] },
  { value: LanguageCode.JA, text: LANGUAGE_MAP[LanguageCode.JA] },
  { value: LanguageCode.KO, text: LANGUAGE_MAP[LanguageCode.KO] },
  { value: LanguageCode.ES, text: LANGUAGE_MAP[LanguageCode.ES] },
  { value: LanguageCode.FR, text: LANGUAGE_MAP[LanguageCode.FR] },
  { value: LanguageCode.DE, text: LANGUAGE_MAP[LanguageCode.DE] },
  { value: LanguageCode.PT, text: LANGUAGE_MAP[LanguageCode.PT] },
  { value: LanguageCode.RU, text: LANGUAGE_MAP[LanguageCode.RU] },
  { value: LanguageCode.AR, text: LANGUAGE_MAP[LanguageCode.AR] },
  { value: LanguageCode.TH, text: LANGUAGE_MAP[LanguageCode.TH] },
  { value: LanguageCode.VI, text: LANGUAGE_MAP[LanguageCode.VI] },
  { value: LanguageCode.ID, text: LANGUAGE_MAP[LanguageCode.ID] },
  { value: LanguageCode.HI, text: LANGUAGE_MAP[LanguageCode.HI] },
];

/**
 * 常用语言选项（仅中英文，用于语言切换等场景）
 */
export const COMMON_LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: LanguageCode.ZH_CN, text: LANGUAGE_MAP[LanguageCode.ZH_CN] },
  { value: LanguageCode.EN, text: LANGUAGE_MAP[LanguageCode.EN] },
];

/**
 * i18n 使用的语言选项（使用简短代码 zh/en）
 * 用于与 i18next 的语言设置保持兼容
 */
export const I18N_LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: 'zh', text: '简体中文' },
  { value: 'en', text: 'English' },
  { value: 'tw', text: '繁体中文' },
];

/**
 * 默认语言代码
 */
export const DEFAULT_LANGUAGE = LanguageCode.ZH_CN;

/**
 * 获取语言的本地化名称
 * 
 * @param code - 语言代码
 * @param withEmoji - 是否包含表情符号
 * @returns 语言的本地化名称，如果不存在则返回代码本身
 * 
 * @example
 * ```ts
 * getLanguageName('zh-CN') // "简体中文"
 * getLanguageName('zh-CN', true) // "🇨🇳 简体中文"
 * getLanguageName('en') // "English"
 * getLanguageName('unknown') // "unknown"
 * ```
 */
export function getLanguageName(code: string, withEmoji: boolean = false): string {
  const map = withEmoji ? LANGUAGE_MAP_WITH_EMOJI : LANGUAGE_MAP;
  
  // 支持大小写不敏感查找
  const lowerCode = code.toLowerCase();
  const entry = Object.entries(map).find(
    ([key]) => key.toLowerCase() === lowerCode
  );
  return entry ? entry[1] : code;
}

/**
 * 检查语言代码是否被支持
 * 
 * @param code - 语言代码
 * @returns 是否支持该语言
 * 
 * @example
 * ```ts
 * isSupportedLanguage('zh-CN') // true
 * isSupportedLanguage('xyz') // false
 * ```
 */
export function isSupportedLanguage(code: string): boolean {
  const lowerCode = code.toLowerCase();
  return Object.keys(LANGUAGE_MAP).some(key => key.toLowerCase() === lowerCode);
}

/**
 * 根据指定的语言代码列表获取语言选项
 * 
 * @param codes - 语言代码数组
 * @param withEmoji - 是否在显示名称中包含表情符号
 * @returns 对应的语言选项数组
 * 
 * @example
 * ```ts
 * getLanguageOptions(['zh-CN', 'en'])
 * // [
 * //   { value: 'zh-CN', text: '简体中文' },
 * //   { value: 'en', text: 'English' }
 * // ]
 * ```
 */
export function getLanguageOptions(codes: string[], withEmoji: boolean = false): LanguageOption[] {
  const map = withEmoji ? LANGUAGE_MAP_WITH_EMOJI : LANGUAGE_MAP;
  
  return codes
    .map(code => {
      // 大小写不敏感查找
      const lowerCode = code.toLowerCase();
      const entry = Object.entries(map).find(
        ([key]) => key.toLowerCase() === lowerCode
      );
      
      if (entry) {
        return { value: entry[0], text: entry[1] };
      }
      return undefined;
    })
    .filter((opt): opt is LanguageOption => opt !== undefined);
}

/**
 * 获取语言映射对象（只包含指定的语言）
 * 
 * @param codes - 语言代码数组，如果不提供则返回所有语言
 * @param withEmoji - 是否包含表情符号
 * @returns 语言代码到名称的映射对象
 * 
 * @example
 * ```ts
 * getLanguageMap(['zh-CN', 'en'])
 * // { 'zh-CN': '简体中文', 'en': 'English' }
 * ```
 */
export function getLanguageMap(codes?: string[], withEmoji: boolean = false): LanguageMap {
  const sourceMap = withEmoji ? LANGUAGE_MAP_WITH_EMOJI : LANGUAGE_MAP;
  
  if (!codes) {
    return { ...sourceMap };
  }

  const result: LanguageMap = {};
  const lowerCodes = codes.map(c => c.toLowerCase());
  
  Object.entries(sourceMap).forEach(([key, value]) => {
    if (lowerCodes.includes(key.toLowerCase())) {
      result[key] = value;
    }
  });
  
  return result;
}

/**
 * 将完整的语言代码转换为 i18n 使用的简短代码
 * 
 * @param code - 完整的语言代码（如 "zh-CN"）
 * @returns i18n 使用的简短代码（如 "zh"）
 * 
 * @example
 * ```ts
 * toI18nCode('zh-CN') // "zh"
 * toI18nCode('en') // "en"
 * ```
 */
export function toI18nCode(code: string): string {
  const lowerCode = code.toLowerCase();
  if (lowerCode.startsWith('zh')) {
    return 'zh';
  }
  if (lowerCode.startsWith('en')) {
    return 'en';
  }
  return code;
}

/**
 * 将 i18n 的简短代码转换为完整的语言代码
 * 
 * @param i18nCode - i18n 使用的简短代码（如 "zh"）
 * @returns 完整的语言代码（如 "zh-CN"）
 * 
 * @example
 * ```ts
 * fromI18nCode('zh') // "zh-CN"
 * fromI18nCode('en') // "en"
 * ```
 */
export function fromI18nCode(i18nCode: string): string {
  const lowerCode = i18nCode.toLowerCase();
  if (lowerCode === 'zh') {
    return LanguageCode.ZH_CN;
  }
  if (lowerCode === 'en') {
    return LanguageCode.EN;
  }
  return i18nCode;
}


